import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import log from "loglevel";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLazyGetJwtQuery, useLazyInitializeTokenQuery, useLogOutIdentQuery } from "../app/api/authenticationApi";
import {
  login,
  selectIsAuthenticated,
  selectIsInitializeToken,
  selectLogOutRequired,
  selectRedirectRequired,
  selectRedirectUrl,
  setRedirectRequired,
} from "../app/slices/authSlice";
import { useAppDispatch, useTypedSelector } from "../app/stores/store";
import envSettings from "../services/system/envSettings";
import { cleanUrlParameters } from "../services/system/urlManager";

export const useAuthInit = (currentPath: string) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isJwtAuthRequestAllowed, setIsJwtAuthRequestAllowed] = useState(false);

  const isAuthenticated = useTypedSelector(selectIsAuthenticated);
  const isLogOutRequested = useTypedSelector(selectLogOutRequired);
  const isRedirectRequired = useTypedSelector(selectRedirectRequired);
  const redirectUrl = useTypedSelector(selectRedirectUrl);
  const isAuthTokenInitialized = useTypedSelector(selectIsInitializeToken);

  const [searchParams] = useSearchParams();

  // The query parameters used when the MidId redirects the citizen to the elo-web, the citizen's session is created in the MidId.
  const codeVerifier = searchParams.get("cv");
  const pkce = searchParams.get("code");
  const rkhash = searchParams.get("rkhash");
  const isAppStartedByPassingOpenIdParams = !!codeVerifier && !!pkce;

  // The query parameter used only by the caseworker to login on behalf of citizen.
  // It is a bearer token passed from the TSB main dashboard. A new token for elo-web is created based on this token.
  const token = searchParams.get("token");

  const dispatch = useAppDispatch();

  const [
    initTokenTrigger,
    { data: tokenWithoutLogin, isLoading: isLoadingTokenWithoutLogin, error: errorTokenWithoutLogin },
  ] = useLazyInitializeTokenQuery();

  const [getJwtTrigger, { data: tokenAfterLogin, isLoading: isLoadingTokenAfterLogin, error: errorTokenAfterLogin }] =
    useLazyGetJwtQuery();

  const { isLoading: isLoadingLogOut, error: errorLogOut } = useLogOutIdentQuery(undefined, {
    skip: !isLogOutRequested,
  });

  useEffect(() => {
    if (isRedirectRequired) {
      dispatch(setRedirectRequired(false));
      dispatch(() => {
        window.location.replace(redirectUrl || envSettings.LOG_OFF_EXT);
      });
    }
  }, [dispatch, isRedirectRequired, redirectUrl]);

  // This useEffect use used then user navigates to the Boliskattelaan (ELO-web) application directly from its browser by e.g. bookmarked URL,
  // not being redirected from the MitId. The credentials object is null, so the app tries to invoke "initialize-endpoint" with empty ?token= which retuens 401.
  // The 401 error code is information for the app to redirect to the MidID page.
  // It is used also when user clicks "Log off" button, then app tries to invoke once the flow from above, finally being also redirected to MidID.
  useEffect(() => {
    // isAuthTokenInitialized is true, when the object with credentials exist in the store

    // isAppStartedByPassingOpenIdParams = true, when:
    // browser is redirected to the app with two OpenId parameters: ?cv= (codeVerifier) and &code= (pkce)
    if (!isAuthTokenInitialized && !isAppStartedByPassingOpenIdParams) {
      log.debug("The auth credentials object (token) does not exist. Triggering the initialize-token endpoint.");

      initTokenTrigger({
        token: token ?? "",
      });
    }
  }, [initTokenTrigger, isAuthTokenInitialized]);

  // This useEffect handles the token reload, if the caseworker logs in to the system on behalf of citizen
  // to see the same views as citizen see on its computer. The bearer token is generated by the TSB main app's dashboard
  // and passed in the URL query param to create a new session in the ELO-web app.
  useEffect(() => {
    // isAppStartedByPassingBearerToken is true, when:
    // (browser is redirected to the root page "/" AND the ?token= parameter passed in the URL)

    // isAppStartedByPassingOpenIdParams = true, when:
    // browser is redirected to the app with two OpenId parameters: ?cv= (codeVerifier) and &code= (pkce)
    const isAppStartedByPassingBearerToken = currentPath === "/" && token !== null;
    if (isAppStartedByPassingBearerToken && !isAppStartedByPassingOpenIdParams) {
      log.debug("The token parameter is passed in the URL. Triggering the initialize-token endpoint.");

      initTokenTrigger({
        token: token ?? "",
      });
    }
  }, [initTokenTrigger, token]);

  // This useEffect handles the token regular access to the app by the citizen. The common auth server (MitID)
  // creates a session, and redirects to the ELO-web with the standard OpenID params: cv and pkce. Then the app
  // can get a JWT token based on these values.
  useEffect(() => {
    // isAppStartedByPassingOpenIdParams = true, when:
    // browser is redirected to the app with two OpenId parameters: ?cv= (codeVerifier) and &code= (pkce)

    // isAuthenticated is:
    // true: when user is being authenticated (the auth object containing bearer token is filled in the store)
    // false: when request to the log-out-ident enpoint is finished successfully (20x) or rejected (40x)

    // isJwtAuthRequestAllowed:
    // true: when user is clicked Log Off button
    // false: when user is being logged in the the app

    if (isAppStartedByPassingOpenIdParams && !isAuthenticated && !isJwtAuthRequestAllowed) {
      log.debug(
        "The user is logged off and open-id parameters are passed in the URL. Triggering the get-jwt endpoint."
      );
      getJwtTrigger({
        pkce: pkce,
        codeVerifier: codeVerifier,
        rkhash: rkhash ?? "",
      });
    }
  }, [
    codeVerifier,
    getJwtTrigger,
    isAuthenticated,
    isJwtAuthRequestAllowed,
    isLogOutRequested,
    pkce,
    rkhash,
    searchParams,
  ]);

  // login by the token parameter (caseworker)
  useEffect(() => {
    const jwtToken = tokenWithoutLogin?.initialToken;
    log.debug("Initialize the access token without a login process.");

    if (jwtToken) {
      cleanUrlParameters();
      dispatch(login(jwtToken));
    }
  }, [dispatch, tokenWithoutLogin]);

  // login by the OpenID parameters (regular citizen)
  useEffect(() => {
    const initToken = tokenAfterLogin?.initialToken;
    log.debug("Initialize the access token with a regular login process.");

    if (initToken) {
      cleanUrlParameters();
      dispatch(login(initToken));
    }
  }, [dispatch, tokenAfterLogin]);

  useEffect(() => {
    const isAppLoading = isLoadingTokenWithoutLogin || isLoadingTokenAfterLogin || isLoadingLogOut;
    setIsLoading(isAppLoading);
  }, [isLoadingLogOut, isLoadingTokenAfterLogin, isLoadingTokenWithoutLogin]);

  useEffect(() => {
    setIsError(
      (errorTokenWithoutLogin && (errorTokenWithoutLogin as FetchBaseQueryError).status !== 401 ? true : false) ||
        (errorTokenAfterLogin && (errorTokenAfterLogin as FetchBaseQueryError).status !== 401 ? true : false) ||
        (errorLogOut && (errorLogOut as FetchBaseQueryError).status !== 401 ? true : false)
    );
  }, [errorTokenWithoutLogin, errorTokenAfterLogin, errorLogOut]);

  useEffect(() => {
    if (isLogOutRequested) {
      setIsJwtAuthRequestAllowed(true);
    }
  }, [isLogOutRequested]);

  return [isLoading, isError] as const;
};
